import { useFormik } from 'formik';
import { isEqual } from 'lodash-es';
import { useState } from 'react';
import { object, string } from 'yup';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { type DiscoverMovieFilters } from '#types.ts';
import { type Mark } from '../../Form/RangeSlider/types.ts';
import { FormActions } from '../FormActions/index.tsx';
import { MovieFilters } from '../MovieFilters/index.tsx';
import { createMovieRangeSlidersConfig } from '../helpers/createRangeSlidersConfig.ts';
import { transformMovieValuestoFilters } from '../helpers/transformValuestoFilters.ts';
import { Form } from '../styled.ts';
import { type MovieFormikValues, type Option } from '../types.ts';

export type MovieFormikProps = {
  addToShortcuts?: boolean;
  filterOptions: Record<string, Mark[] | Option[]>;
  filtersParam: DiscoverMovieFilters;
  initialValues: MovieFormikValues;
  setFiltersParam: (value?: DiscoverMovieFilters, pathnameOverride?: string) => void;
  setOpenFiltersDrawer: (value: boolean) => void;
};

export const MovieFormik = ({
  addToShortcuts: initialAddToShortcuts,
  filterOptions,
  filtersParam,
  initialValues,
  setFiltersParam,
  setOpenFiltersDrawer,
}: MovieFormikProps) => {
  const { config } = useToolbelt();

  const formik = useFormik<MovieFormikValues & { addToShortcuts: boolean; shortcutName: string }>({
    initialValues: {
      ...initialValues,
      addToShortcuts: initialAddToShortcuts ?? false,
      shortcutName: '',
    },
    onSubmit: ({ addToShortcuts, shortcutName, ...otherValues }) => {
      const newFiltersParam = transformMovieValuestoFilters(otherValues, filterOptions, config);

      if (!isEqual(filtersParam, newFiltersParam)) {
        setFiltersParam({ ...(addToShortcuts ? { ...newFiltersParam, shortcutName } : newFiltersParam) }, 'movie');
        setOpenFiltersDrawer(false);
      }
    },
    validationSchema: object({
      shortcutName: string().when('addToShortcuts', {
        is: true,
        // Part of formik API
        // eslint-disable-next-line unicorn/no-thenable
        then: schema => schema.required('Enter a shortcut name'),
      }),
    }),
  });

  const initialRangeSliders = createMovieRangeSlidersConfig(initialValues);
  const [rangeSliders, setRangeSliders] = useState(initialRangeSliders);

  return (
    <Form onSubmit={formik.handleSubmit}>
      <MovieFilters
        config={{ rangeSliders }}
        filterOptions={filterOptions}
        formik={formik}
        onRangeSliderToggle={(_e, name, checked, defaultValue) => {
          void formik.setFieldValue(name, checked ? defaultValue : []);

          setRangeSliders({
            ...rangeSliders,
            [name]: { ...rangeSliders[name], disabled: !checked },
          });
        }}
      />
      <FormActions
        filtersParam={filtersParam}
        formik={formik}
        setFiltersParam={setFiltersParam}
        setOpenFiltersDrawer={setOpenFiltersDrawer}
      />
    </Form>
  );
};
